/**
 * AGC: PVE Selection Page Component
 * AGC: Allows players to choose between Hybrid or On-Chain battle modes
 * AGC: Uses carousel similar to Marketplace for mode selection
 */

import React, { useState, useRef, useCallback, useEffect } from 'react'
import type { ActivePage } from './MainMenu'
import type { BottomNavKey } from './BottomNav'

interface PVESelectionProps {
  onNavigateToPage?: (page: ActivePage) => void
  onNavClick: (nav: BottomNavKey) => void
  activeNav: BottomNavKey
  onOpenSuggestion: () => void
  onOpenSettings: () => void
  onOpenInventory: () => void
  onModeSelected?: (mode: 'onchain' | 'hybrid', data: { battleId: string; difficulty: string; selectedBuffs: number[]; playerLevel: number }) => void
  modeData?: { battleId: string; difficulty: string; selectedBuffs: number[]; playerLevel: number } | null
}

interface BattleMode {
  id: 'hybrid' | 'onchain'
  name: string
  subtitle: string
  description: string
  features: string[]
  gasEstimate: string
  badge: string
  badgeColor: string
  gradient: string
  borderColor: string
  buttonColor: string
  buttonHover: string
  customGradient?: string
}

const BATTLE_MODES: BattleMode[] = [
  {
    id: 'hybrid',
    name: 'Hybrid System',
    subtitle: 'RECOMMENDED',
    description: 'Backend simulates battle, final result on-chain (1 tx total)',
    features: [
      '✅ Much cheaper (1 transaction total)',
      '✅ Same game engine logic',
      '✅ Fast battle resolution',
      '✅ WebSocket real-time updates'
    ],
    gasEstimate: 'Est. Gas: 1 transaction only (for final result)',
    badge: 'RECOMMENDED',
    badgeColor: 'bg-green-500',
    gradient: 'from-green-600 to-teal-700',
    borderColor: 'border-green-400',
    buttonColor: 'bg-green-500',
    buttonHover: 'hover:bg-green-600'
  },
  {
    id: 'onchain',
    name: 'Fully On-Chain Battle',
    subtitle: 'BASE-MAXI',
    description: 'Every turn is recorded on blockchain - full transparency!',
    features: [
      '✅ 100% transparent & verifiable',
      '✅ Bot AI runs on-chain',
      '✅ Choose hand/element each turn',
      '⚠️ 1 transaction per turn (~5-10 turns)'
    ],
    gasEstimate: 'Est. Gas: ~5-10 transactions total',
    badge: 'ACTIVE (BETA)',
    badgeColor: 'bg-blue-600',
    gradient: 'from-blue-600 to-blue-700',
    borderColor: 'border-blue-500',
    buttonColor: 'bg-blue-600',
    buttonHover: 'hover:bg-blue-700',
    // AGC: Custom gradient for on-chain mode
    customGradient: 'linear-gradient(to bottom right, #0000FF, #0033FF)'
  }
]

export const PVESelection: React.FC<PVESelectionProps> = ({
  onNavigateToPage,
  onModeSelected,
  modeData
}) => {
  const [currentCardIndex, setCurrentCardIndex] = useState(0)
  const carouselRef = useRef<HTMLDivElement>(null)

  // AGC: Scroll to specific card in carousel
  const scrollToCard = useCallback((index: number) => {
    if (!carouselRef.current) return

    const container = carouselRef.current
    const cardWidth = container.clientWidth * 0.85 // AGC: Card width (85% of viewport)
    const gap = 16 // AGC: Gap between cards
    const scrollPosition = index * (cardWidth + gap)

    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    })

    setCurrentCardIndex(index)
  }, [])

  // AGC: Handle carousel scroll to update current index
  const handleCarouselScroll = useCallback(() => {
    if (!carouselRef.current) return
    
    const container = carouselRef.current
    const scrollLeft = container.scrollLeft
    const cardWidth = container.clientWidth * 0.85
    const gap = 16
    const index = Math.round(scrollLeft / (cardWidth + gap))
    
    if (index !== currentCardIndex && index >= 0 && index < BATTLE_MODES.length) {
      setCurrentCardIndex(index)
    }
  }, [currentCardIndex])

  // AGC: Listen for warning modal request
  useEffect(() => {
    const handleShowWarning = (event: CustomEvent) => {
      const { mode, data } = event.detail
      // AGC: Show warning modal in MainMenu
      window.dispatchEvent(new CustomEvent('mainmenu:show-onchain-warning', { detail: { mode, data } }))
    }

    window.addEventListener('pveselection:show-warning', handleShowWarning as EventListener)
    return () => {
      window.removeEventListener('pveselection:show-warning', handleShowWarning as EventListener)
    }
  }, [])

  // AGC: Handle mode selection
  const handleModeSelect = useCallback((mode: 'onchain' | 'hybrid') => {
    console.log(`[PVESelection] Mode selected: ${mode}`, modeData)
    if (onModeSelected) {
      onModeSelected(mode, modeData || null)
    }
  }, [modeData, onModeSelected])

  // AGC: Top padding style matching Profile.tsx - 72px to clear TopNav (60px) + spacing
  const sharedTopPaddingStyle = { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 72px)' }

  return (
    <main
      className="pb-[88px] px-3 text-white flex flex-col gap-1.5"
      style={sharedTopPaddingStyle}
    >
      {/* AGC: Hero Header (No Card) - Following Profile.tsx pattern */}
      <section className="pb-2 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">SELECT YOUR MODE</p>
            <h1 className="text-2xl font-bold leading-tight text-white">Battle Mode</h1>
            <p className="text-[11px] text-slate-400">Choose how you want to battle</p>
          </div>
        </div>
      </section>

      {/* AGC: Carousel Container */}
      <div className="flex-1 flex items-center justify-center min-h-[60vh]">
        <div
          ref={carouselRef}
          onScroll={handleCarouselScroll}
          className="w-full overflow-x-auto scrollbar-hide snap-x snap-mandatory flex gap-4 pb-4"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {BATTLE_MODES.map((mode, index) => (
            <div
              key={mode.id}
              className="flex-shrink-0 w-[85%] snap-center"
            >
              <div
                onClick={() => handleModeSelect(mode.id)}
                className={`cursor-pointer bg-gradient-to-br ${mode.gradient} p-6 rounded-lg border-2 ${mode.borderColor} hover:${mode.borderColor.replace('400', '300')} transition-all duration-200 h-full`}
                style={mode.customGradient ? { background: mode.customGradient } : undefined}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-bold text-white">{mode.name}</h3>
                  <span className={`${mode.badgeColor} text-white text-xs px-2 py-1 rounded`}>
                    {mode.badge}
                  </span>
                </div>
                <p className="text-gray-200 text-sm mb-4">
                  {mode.description}
                </p>
                <ul className="text-sm text-gray-100 space-y-2 mb-4">
                  {mode.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
                <div className={`p-3 ${mode.id === 'hybrid' ? 'bg-green-900 bg-opacity-30' : 'bg-blue-900 bg-opacity-50'} rounded mb-4`}>
                  <p className={`text-xs ${mode.id === 'hybrid' ? 'text-green-200' : 'text-blue-200'}`}>
                    {mode.gasEstimate}
                  </p>
                </div>
                <button className={`w-full ${mode.buttonColor} ${mode.buttonHover} text-white font-bold py-3 px-6 rounded-lg transition-all`}>
                  Start {mode.name === 'Hybrid System' ? 'Hybrid' : 'On-Chain'} Battle
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AGC: Carousel Indicators */}
      <div className="flex justify-center gap-2 mt-4">
        {BATTLE_MODES.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollToCard(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentCardIndex
                ? 'bg-white w-8'
                : 'bg-white/30'
            }`}
            aria-label={`Go to mode ${index + 1}`}
          />
        ))}
      </div>
    </main>
  )
}

